import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import RoomCard from '../components/RoomCard';
import CalendarWidget from '../components/CalendarWidget';
import ReservationWidget from '../components/ReservationWidget';
import LazyImage from '../components/LazyImage';
import image1 from '../images/1.jpg';
import image2 from '../images/2.jpg';
import kupelkahora from '../images/kupelkahore.png';
import spalna1 from '../images/spalna1.png';
import spalna2 from '../images/spalna2.png';
import kupelkadole from '../images/kupelkadole.png';
import schodisko1 from '../images/schodisko1.png';
import sprcha_sauna1 from '../images/sprcha_sauna1.png';
import chodbavstup from '../images/chodbavstup.png';
import chodbickahore from '../images/chodbickahore.png';
import hala from '../images/hala.png';
import kuchyna1 from '../images/kuchyna1.png';
import obyvacka1 from '../images/obyvacka1.png';
import obyvackahore2 from '../images/obyvackahore2.png';
import detskaizba from '../images/detskaizba.png';
import kupelkahore from '../images/kupelkahore.png';
import obyvacka2 from '../images/obyvacka2.png';
import terasa from '../images/terasa.png';
import obyvackahore from '../images/obyvackahorepng.png';
import toaleta from '../images/toaleta.png';

const Accommodation = () => {
  const { t } = useLanguage();

  const [heroRef, heroVisible] = useScrollAnimation({ threshold: 0.2 });
  const [roomsRef, roomsVisible] = useScrollAnimation();
  const [pricingRef, pricingVisible] = useScrollAnimation();
  const [reservationRef, reservationVisible] = useScrollAnimation();
  const [calendarRef, calendarVisible] = useScrollAnimation();

  // Scroll to section if hash is present
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const section = document.getElementById(hash.substring(1));
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, []);

  const rooms = [
    {
      id: 1,
      name: t('accommodation.room1'),
      image: spalna2,
      images: [spalna2, spalna1], // Galéria - prvá je hlavná
      area: '16 m²',
      maxPersons: 3,
      description: t('accommodation.roomsDesc'),
      beds: [
        { type: t('accommodation.doubleBed'), count: 1 },
        { type: `${t('accommodation.extraBed')} (${t('accommodation.foldingChair')})`, count: 1 }
      ],
      bathroom: {
        count: 1,
        toilet: 1,
        equipment: [
          t('accommodation.wc'),
          t('accommodation.shower'),
          t('accommodation.sink'),
          t('accommodation.dryingRack'),
          t('accommodation.towels')
        ]
      },
      location: {
        floor: 2,
        view: [t('accommodation.countrysideView'), t('accommodation.slopeView')]
      }
    },
    {
      id: 2,
      name: t('accommodation.room2'),
      image: detskaizba,
      images: [detskaizba], // Galéria
      area: '18 m²',
      maxPersons: 4,
      description: t('accommodation.roomsDesc'),
      beds: [
        { type: t('accommodation.bunkBed'), count: 2 }
      ],
      location: {
        floor: 1,
        view: [t('accommodation.slopeView')]
      }
    },
    {
      id: 3,
      name: t('accommodation.room3'),
      image: obyvackahore2,
      images: [obyvackahore2, obyvackahore], // Galéria
      area: '20 m²',
      maxPersons: 2,
      description: t('accommodation.roomsDesc'),
      beds: [
        { type: t('accommodation.doubleBed'), count: 1 },
        { type: t('accommodation.sofaBed'), count: 1 }
      ],
      location: {
        floor: 1,
        view: [t('accommodation.spaciousRoom')]
      }
    },
    {
      id: 4,
      name: t('accommodation.room4'),
      image: obyvacka1,
      images: [obyvacka1, obyvacka2], // Galéria
      area: '10-25 m²',
      description: t('accommodation.roomsDesc'),
    },
    {
      id: 5,
      name: t('accommodation.kitchen'),
      image: kuchyna1,
      images: [kuchyna1], // Galéria
      area: '15-30 m²',
      description: t('accommodation.amenitiesDesc'),
    },
    {
      id: 6,
      name: t('accommodation.dining'),
      image: hala,
      images: [hala], // Galéria
      area: '15-30 m²',
      description: t('accommodation.amenitiesDesc'),
    },
    {
      id: 7,
      name: t('accommodation.bathroom'),
      image: kupelkahore,
      images: [kupelkahore], // Galéria
      area: '5-10 m²',
      description: t('accommodation.roomsDesc'),
    },
    {
      id: 10,
      name: t('accommodation.bathroom2'),
      image: kupelkadole,
      images: [kupelkadole], // Galéria - fotky doplní používateľ
      area: '5-10 m²',
      description: t('accommodation.roomsDesc'),
    },
    {
      id: 11,
      name: t('accommodation.bathroom3'),
      image: toaleta,
      images: [toaleta], // Galéria - fotky doplní používateľ
      area: '5-10 m²',
      description: t('accommodation.roomsDesc'),
    },
    {
      id: 8,
      name: t('accommodation.wardrobe'),
      image: chodbickahore,
      images: [chodbickahore], // Galéria
      area: '5-10 m²',
      description: t('accommodation.roomsDesc'),
    },
    {
      id: 12,
      name: t('accommodation.skiRoom'),
      image: chodbavstup,
      images: [chodbavstup], // Galéria - fotky doplní používateľ
      area: '5-10 m²',
      description: t('accommodation.roomsDesc'),
    },
    {
      id: 9,
      name: t('accommodation.terrace'),
      image: terasa,
      images: [terasa], // Galéria
      area: '20-30 m²',
      description: t('accommodation.amenitiesDesc'),
    },
  ];

  const scrollToRooms = () => {
    const roomsSection = document.getElementById('rooms-section');
    if (roomsSection) {
      roomsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToCalendar = () => {
    const calendarSection = document.getElementById('calendar-section');
    if (calendarSection) {
      calendarSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToReservation = () => {
    const reservationSection = document.getElementById('reservation-section');
    if (reservationSection) {
      reservationSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet>
        <title>Ubytovanie – Chata Barvinok Litmanová</title>
        <meta
          name="description"
          content="Komfortná chata Barvinok v Litmanovej: 4 spálne, 8 lôžok + 4 prístelky, 3 kúpeľne, 2 krby a moderné vybavenie pre rodiny a skupiny."
        />
      </Helmet>
      <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className={`relative min-h-[120vh] flex items-center justify-center overflow-hidden pt-24 transition-all duration-800 ${heroVisible ? 'animate-fade-in opacity-100' : 'opacity-0'}`}
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <LazyImage 
            src={image2} 
            alt={t('accommodation.title')}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-black/60"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className={`text-center mb-8 md:mb-12 transition-all duration-800 delay-200 ${heroVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}>
            <span className="text-copper-400 text-xs md:text-sm font-semibold uppercase tracking-widest mb-3 md:mb-4 block">
              {t('nav.accommodation')}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 md:mb-6 px-4">
              {t('accommodation.title')}
            </h1>
            <div className="w-24 h-1 bg-copper-500 mx-auto mb-3 md:mb-4"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-200 max-w-2xl mx-auto mb-6 md:mb-8 px-4">
              {t('accommodation.subtitle')}
            </p>
          </div>

          {/* Information Cards */}
          <div className={`grid md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8 transition-all duration-800 delay-300 ${heroVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}>
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-4 md:p-6 lg:p-8 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-copper-500 transition-colors">
              <div className="flex items-center space-x-3 md:space-x-4 mb-3 md:mb-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-copper-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 md:w-6 md:h-6 text-copper-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-5.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
                <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">{t('accommodation.amenities')}</h3>
              </div>
              <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 leading-relaxed mb-3 md:mb-4">
                {t('accommodation.amenitiesDesc')}
              </p>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-copper-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{t('accommodation.bedsCount')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-copper-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{t('accommodation.bedrooms')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-copper-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{t('accommodation.bathroomsCount')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-copper-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{t('accommodation.wifi')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-copper-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{t('accommodation.twoSaunas')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-copper-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{t('accommodation.sauna')}</span>
                </li>
              </ul>
            </div>

            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-4 md:p-6 lg:p-8 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-copper-500 transition-colors">
              <div className="flex items-center space-x-3 md:space-x-4 mb-3 md:mb-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-copper-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 md:w-6 md:h-6 text-copper-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">{t('accommodation.capacity')}</h3>
              </div>
              <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 leading-relaxed mb-3 md:mb-4">
                {t('accommodation.capacityDesc')}
              </p>
              <div className="mt-3 md:mt-4 text-copper-400 font-semibold text-base md:text-lg mb-3 md:mb-4">
                {t('accommodation.capacityDetails')}
              </div>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-copper-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{t('accommodation.partiallyFenced')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>{t('accommodation.petsNotAllowed')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-copper-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{t('accommodation.familyFriendly')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-copper-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{t('accommodation.restaurantNearby')}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Three smaller cards */}
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-8 transition-all duration-800 delay-400 ${heroVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}>
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-4 md:p-6 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-copper-500 transition-all">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-copper-500/20 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <svg className="w-6 h-6 md:w-7 md:h-7 text-copper-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-2">{t('accommodation.comfort')}</h3>
              <p className="text-xs md:text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                {t('accommodation.comfortDesc')}
              </p>
            </div>

            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-4 md:p-6 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-copper-500 transition-all">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-copper-500/20 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <svg className="w-6 h-6 md:w-7 md:h-7 text-copper-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-2">{t('accommodation.safety')}</h3>
              <p className="text-xs md:text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                {t('accommodation.safetyDesc')}
              </p>
            </div>

            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-4 md:p-6 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-copper-500 transition-all sm:col-span-2 lg:col-span-1">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-copper-500/20 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <svg className="w-6 h-6 md:w-7 md:h-7 text-copper-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-2">{t('accommodation.experience')}</h3>
              <p className="text-xs md:text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                {t('accommodation.experienceDesc')}
              </p>
            </div>
          </div>

          {/* Rooms, Calendar and Reservations Buttons */}
          <div className={`text-center transition-all duration-800 delay-500 -mt-8 md:mt-0 px-4 py-4 ${heroVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
              <button
                onClick={scrollToRooms}
                className="w-auto max-w-[280px] sm:w-auto px-6 md:px-8 py-3 md:py-4 bg-copper-500 text-white font-semibold hover:bg-copper-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 uppercase tracking-wide text-sm md:text-base"
              >
                {t('accommodation.rooms')}
              </button>
              <button
                onClick={scrollToCalendar}
                className="w-auto max-w-[280px] sm:w-auto px-6 md:px-8 py-3 md:py-4 bg-copper-500 text-white font-semibold hover:bg-copper-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 uppercase tracking-wide text-sm md:text-base"
              >
                {t('accommodation.calendar')}
              </button>
              <button
                onClick={scrollToReservation}
                className="w-auto max-w-[280px] sm:w-auto px-6 md:px-8 py-3 md:py-4 bg-copper-500 text-white font-semibold hover:bg-copper-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 uppercase tracking-wide text-sm md:text-base"
              >
                {t('accommodation.bookingTitle')}
              </button>
              <button
                onClick={scrollToPricing}
                className="w-auto max-w-[280px] sm:w-auto px-6 md:px-8 py-3 md:py-4 bg-copper-500 text-white font-semibold hover:bg-copper-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 uppercase tracking-wide text-sm md:text-base"
              >
                {t('accommodation.pricing')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Rooms Section */}
      <section 
        id="rooms-section"
        ref={roomsRef}
        className={`py-16 md:py-24 bg-gray-200 dark:bg-gray-800/50 transition-all duration-800 ${roomsVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}
      >
        <div className="max-w-[120rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 md:mb-6">
              {t('accommodation.rooms')}
            </h2>
            <div className="w-24 h-1 bg-copper-500 mx-auto"></div>
          </div>

          {/* Rooms Grid */}
          <div className={`grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 transition-all duration-800 delay-200 ${roomsVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}>
            {rooms.map((room) => (
              <RoomCard key={room.id} room={room} />
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section 
        id="pricing-section"
        ref={pricingRef}
        className={`py-16 md:py-24 bg-gray-100 dark:bg-gray-900 transition-all duration-800 ${pricingVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 md:mb-6">
              {t('accommodation.pricing')}
            </h2>
            <div className="w-24 h-1 bg-copper-500 mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6 md:mb-8 max-w-3xl mx-auto">
              {t('accommodation.pricingNote')}
            </p>
          </div>

          {/* Pricing Table */}
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden shadow-lg rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-copper-500">
                    <tr>
                      <th scope="col" className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-white uppercase tracking-wider">
                        {t('accommodation.pricing')}
                      </th>
                      <th scope="col" className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-white uppercase tracking-wider">
                        {t('accommodation.period')}
                      </th>
                      <th scope="col" className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm font-semibold text-white uppercase tracking-wider">
                        {t('accommodation.price')}
                      </th>
                      <th scope="col" className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-white uppercase tracking-wider">
                        {t('accommodation.conditions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {/* Winter Season */}
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm md:text-base font-medium text-gray-900 dark:text-white">
                        {t('accommodation.winterSeason')}
                      </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm md:text-base text-gray-700 dark:text-gray-300">
                        {t('accommodation.winterSeasonDates')}
                      </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-center">
                        <span className="text-lg md:text-xl font-bold text-copper-500">{t('accommodation.winterSeasonPrice')}</span>
                        <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">{t('accommodation.winterSeasonDetails')}</div>
                      </td>
                      <td className="px-4 md:px-6 py-4 text-sm md:text-base text-gray-700 dark:text-gray-300">
                        {t('accommodation.winterSeasonMin')}
                      </td>
                    </tr>
                    {/* Off Season */}
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm md:text-base font-medium text-gray-900 dark:text-white">
                        {t('accommodation.offSeason')}
                      </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm md:text-base text-gray-700 dark:text-gray-300">
                        {t('accommodation.offSeasonDates')}
                      </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-center">
                        <span className="text-lg md:text-xl font-bold text-copper-500">{t('accommodation.offSeasonPrice')}</span>
                        <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">{t('accommodation.offSeasonDetails')}</div>
                      </td>
                      <td className="px-4 md:px-6 py-4 text-sm md:text-base text-gray-700 dark:text-gray-300">
                        {t('accommodation.offSeasonMin')}
                      </td>
                    </tr>
                    {/* Easter Stay */}
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm md:text-base font-medium text-gray-900 dark:text-white">
                        {t('accommodation.easterStay')}
                      </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm md:text-base text-gray-700 dark:text-gray-300">
                        {t('accommodation.easterStayDates')}
                      </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-center">
                        <span className="text-lg md:text-xl font-bold text-copper-500">{t('accommodation.easterStayPrice')}</span>
                        <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">{t('accommodation.easterStayDetails')}</div>
                      </td>
                      <td className="px-4 md:px-6 py-4 text-sm md:text-base text-gray-700 dark:text-gray-300">
                        {t('accommodation.easterStayMin')}
                      </td>
                    </tr>
                    {/* Summer Season */}
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm md:text-base font-medium text-gray-900 dark:text-white">
                        {t('accommodation.summerSeason')}
                      </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm md:text-base text-gray-700 dark:text-gray-300">
                        {t('accommodation.summerSeasonDates')}
                      </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-center">
                        <span className="text-lg md:text-xl font-bold text-copper-500">{t('accommodation.summerSeasonPrice')}</span>
                        <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">{t('accommodation.summerSeasonDetails')}</div>
                      </td>
                      <td className="px-4 md:px-6 py-4 text-sm md:text-base text-gray-700 dark:text-gray-300">
                        {t('accommodation.summerSeasonMin')}
                      </td>
                    </tr>
                    {/* Winter Off Season */}
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm md:text-base font-medium text-gray-900 dark:text-white">
                        {t('accommodation.winterOffSeason')}
                      </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm md:text-base text-gray-700 dark:text-gray-300">
                        {t('accommodation.winterOffSeasonDates')}
                      </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-center">
                        <span className="text-lg md:text-xl font-bold text-copper-500">{t('accommodation.winterOffSeasonPrice')}</span>
                        <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">{t('accommodation.winterOffSeasonDetails')}</div>
                      </td>
                      <td className="px-4 md:px-6 py-4 text-sm md:text-base text-gray-700 dark:text-gray-300">
                        {t('accommodation.winterOffSeasonMin')}
                      </td>
                    </tr>
                    {/* Christmas Stay */}
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm md:text-base font-medium text-gray-900 dark:text-white">
                        {t('accommodation.christmasStay')}
                      </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm md:text-base text-gray-700 dark:text-gray-300">
                        {t('accommodation.christmasStayDates')}
                      </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-center">
                        <span className="text-lg md:text-xl font-bold text-copper-500">{t('accommodation.christmasStayPrice')}</span>
                        <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">{t('accommodation.christmasStayDetails')}</div>
                      </td>
                      <td className="px-4 md:px-6 py-4 text-sm md:text-base text-gray-700 dark:text-gray-300">
                        {t('accommodation.christmasStayMin')}
                      </td>
                    </tr>
                    {/* New Year */}
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors bg-copper-50/30 dark:bg-copper-900/20">
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm md:text-base font-bold text-gray-900 dark:text-white">
                        {t('accommodation.newYear')}
                      </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm md:text-base text-gray-700 dark:text-gray-300">
                        {t('accommodation.newYearDates')}
                      </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-center">
                        <span className="text-lg md:text-xl font-bold text-copper-500">{t('accommodation.newYearPrice')}</span>
                        <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">{t('accommodation.newYearDetails')}</div>
                      </td>
                      <td className="px-4 md:px-6 py-4 text-sm md:text-base text-gray-700 dark:text-gray-300">
                        {t('accommodation.newYearMin')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reservation Section */}
      <section 
        id="reservation-section"
        ref={reservationRef}
        className={`py-16 md:py-24 bg-white dark:bg-gray-800 transition-all duration-800 ${reservationVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 md:mb-6">
              {t('accommodation.bookingTitle')}
            </h2>
            <div className="w-24 h-1 bg-copper-500 mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4 md:mb-6 max-w-2xl mx-auto">
              {t('accommodation.bookingDesc')}
            </p>
            <div className="flex flex-wrap justify-center gap-3 md:gap-4 text-sm md:text-base text-gray-600 dark:text-gray-400 mb-6 md:mb-8">
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-copper-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                <span>{t('accommodation.checkIn')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-copper-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                <span>{t('accommodation.checkOut')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-copper-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                </svg>
                <span>{t('accommodation.minStay')}</span>
              </div>
            </div>
          </div>
          <ReservationWidget />
        </div>
      </section>

      {/* Calendar Section */}
      <section 
        id="calendar-section"
        ref={calendarRef}
        className={`py-16 md:py-24 bg-gray-100 dark:bg-gray-900 transition-all duration-800 ${calendarVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 md:mb-6">
              {t('accommodation.calendar')}
            </h2>
            <div className="w-24 h-1 bg-copper-500 mx-auto"></div>
          </div>
          <CalendarWidget />
        </div>
      </section>
    </div>
    </>
  );
};

export default Accommodation;

